/**
 * brands/index.ts
 * ---------------
 * Single place to register brand config overrides.
 *
 * To add a new brand:
 *   1. Create a folder: src/config/brands/<brand-name>/
 *   2. Drop JSON files named by service: shopify.json, amplify.json, etc.
 *   3. Import and register them below.
 *
 * To remove a brand:
 *   1. Delete its entry below (and optionally the folder).
 *
 * Everything else (configService, brandDetection, shopifyApi, etc.)
 * automatically picks up the change — no other files to edit.
 */

// ─── Brand config imports ────────────────────────────────────────────────────
import orientalweaversShopify from './orientalweavers/shopify.json';
import flexsteelShopify       from './flexsteel/shopify.json';
import homestylesShopify      from './homestyles/shopify.json';
import gtrAmplify             from './gtr/amplify.json';
import dynamicRugsAmplify     from './dynamic-rugs/amplify.json';

// ─── Brand overrides registry ────────────────────────────────────────────────
// Key   = brand slug (used in URLs, Caddy paths, x-vendor header)
// Value = partial map of service name → override config JSON
//
// 'nestingale' is the default brand and needs no overrides (uses defaults).

export const BRAND_OVERRIDES: Record<string, Record<string, unknown>> = {
  nestingale:      {},
  orientalweavers: { shopify: orientalweaversShopify },
  flexsteel:       { shopify: flexsteelShopify },
  homestyles:      { shopify: homestylesShopify },
  gtr:             { amplify: gtrAmplify },
  'dynamic-rugs':  { amplify: dynamicRugsAmplify },
};

/** Set of all registered brand keys. */
export const BRAND_KEYS = new Set<string>(Object.keys(BRAND_OVERRIDES));

/** Returns true if the value is a registered brand key. */
export function isBrandKey(value: string): boolean {
  return BRAND_KEYS.has(value);
}
